import { useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { firebaseConfig, vapidKey, isFirebaseConfigured } from "../config/firebase";
import { registerFCMDevice } from "../utils/api";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";

/**
 * Hook that initializes Firebase Cloud Messaging for push notifications.
 *
 * - Registers the service worker
 * - Requests notification permission (only when explicitly triggered)
 * - Registers the FCM token with the backend
 * - Handles foreground messages with toast notifications
 */
const useFirebaseMessaging = () => {
  const { user } = useAuth();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const messagingRef = useRef(null);
  const tokenRegistered = useRef(false);

  // Request permission and register token
  const requestPermission = useCallback(async () => {
    if (!isFirebaseConfigured()) {
      console.warn("Firebase not configured — push notifications disabled.");
      return false;
    }

    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return false;
      }

      const [{ initializeApp, getApps }, { getMessaging, getToken }] =
        await Promise.all([
          import("firebase/app"),
          import("firebase/messaging"),
        ]);

      // Initialize Firebase app if not already done
      const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];

      // Register service worker
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );

      // Send Firebase config to the service worker
      if (registration.active) {
        registration.active.postMessage({
          type: "FIREBASE_CONFIG",
          config: firebaseConfig,
        });
      }
      // Also send when it activates
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener("statechange", () => {
            if (newWorker.state === "activated") {
              newWorker.postMessage({
                type: "FIREBASE_CONFIG",
                config: firebaseConfig,
              });
            }
          });
        }
      });

      const messaging = getMessaging(app);
      messagingRef.current = messaging;

      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (token && !tokenRegistered.current) {
        await registerFCMDevice(axiosPrivate, token, "web");
        tokenRegistered.current = true;
      }

      return true;
    } catch (error) {
      console.error("Failed to initialize Firebase Messaging:", error);
      return false;
    }
  }, [axiosPrivate]);

  // Listen for foreground messages
  useEffect(() => {
    if (!user || !messagingRef.current) return;

    let unsubscribe = null;
    let cancelled = false;

    import("firebase/messaging").then(({ onMessage }) => {
      if (cancelled || !messagingRef.current) return;

      unsubscribe = onMessage(messagingRef.current, (payload) => {
        const { title, body } = payload.notification || {};
        const data = payload.data || {};

        // Notify useUnreadMessages to refresh the badge count immediately
        window.dispatchEvent(new Event("unread-messages-update"));

        toast.info(
          <div
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (data.thread_id) {
                navigate(`/messages/${data.thread_id}`);
              } else {
                navigate("/messages");
              }
            }}
          >
            <strong>{title}</strong>
            <br />
            <span style={{ fontSize: "0.85em", color: "#6B7280" }}>{body}</span>
          </div>,
          {
            autoClose: 8000,
            closeOnClick: false,
          }
        );
      });
    });

    return () => {
      cancelled = true;
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [user, navigate]);

  // Auto-request if permission was already granted
  useEffect(() => {
    if (!user || !isFirebaseConfigured()) return;
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted" && !tokenRegistered.current) {
      requestPermission();
    }
  }, [user, requestPermission]);

  return { requestPermission };
};

export default useFirebaseMessaging;
